/**
 * PURPOSE: Panel de respaldo de las firmas de la Vista Planilla FEDUP (FTR-0062): tarjetas por casilla del modo activo.
 * RESPONSIBILITIES:
 *  - Sí: modo PRINCIPAL de firma (el overlay sobre el visor nativo está desactivado: su zoom/scroll interno no se puede leer y lo
 *    descuadraba); cada tarjeta muestra la etiqueta fija, si está firmada (con la imagen) y abre el pad.
 *  - Sí: con el partido oficializado, solo las tarjetas vacías siguen resaltadas y abren el pad; las firmadas muestran el candado y
 *    el texto en lugar del pad.
 *  - No: no pide nada por sí mismo (las imágenes llegan del controlador) ni toca la planilla.
 * COLLABORATORS:
 *  - FedupPreviewModal.jsx, useFedupSignatureController.js, fedupSignatures.utils.js
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/shared/fedupSignatures/FedupSignaturePanel.jsx
 */

import React, { useEffect } from "react";
import { FiCheckCircle, FiLock, FiX } from "react-icons/fi";

import { OFFICIAL_LOCK_MESSAGE, getModeInfo, isSlotLocked, modeCounter, slotsOfMode } from "./fedupSignatures.utils.js";

export default function FedupSignaturePanel({
  status,
  mode,
  official = false,
  previews = {},
  imageFor,
  ensureImage,
  onSelectSlot,
  lockSlotId = null,
  onClose,
}) {
  const slots = slotsOfMode(status, mode);
  const info = getModeInfo(mode);
  const signedKey = slots.map((s) => `${s.slot}:${s.signature?.id ?? ""}`).join("|");

  useEffect(() => {
    for (const slot of slots) if (slot.signature) ensureImage?.(slot.slot);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- se piden de nuevo solo cuando cambia alguna firma
  }, [signedKey, ensureImage]);

  if (!mode || !info) return null;

  return (
    <aside
      aria-label={info.label}
      data-fedup-signature-panel={mode}
      className="absolute inset-y-0 right-0 z-20 flex w-full max-w-[340px] flex-col border-l border-slate-200 bg-white shadow-xl"
    >
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
        <div className="flex items-baseline gap-2">
          <h3 className="text-xs font-extrabold text-slate-900">{info.label}</h3>
          <span className="text-[10px] font-semibold tabular-nums text-slate-400">{modeCounter(status, mode)}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar el panel de firmas"
          title="Cerrar el panel de firmas"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
        >
          <FiX aria-hidden="true" />
        </button>
      </div>
      <ul className="flex-1 space-y-2 overflow-y-auto p-2">
        {slots.map((slot) => {
          const preview = previews[slot.slot];
          const removed = Boolean(preview?.removed);
          const signed = Boolean(slot.signature) && !removed;
          const src = preview?.dataUrl || (signed ? imageFor?.(slot.slot) : null);
          const locked = isSlotLocked(slot, official);
          return (
            <li key={slot.slot}>
              <button
                type="button"
                data-fedup-signature-card={slot.slot}
                onClick={() => onSelectSlot?.(slot.slot)}
                data-signed={signed ? "true" : "false"}
                data-locked={locked ? "true" : "false"}
                aria-label={`${slot.label}: ${locked ? "firma bloqueada (partido oficializado)" : signed ? "firmada" : "sin firma"}`}
                className={[
                  "flex w-full flex-col gap-1.5 rounded-xl border px-3 py-2 text-left transition",
                  locked ? "cursor-default border-slate-200 bg-slate-50" : "border-emerald-300 bg-white hover:bg-emerald-50/60",
                ].join(" ")}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold text-slate-800">{slot.label}</span>
                  {locked ? (
                    <FiLock className="text-slate-400" data-fedup-signature-lock-icon="true" aria-hidden="true" />
                  ) : signed ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
                      <FiCheckCircle aria-hidden="true" /> Firmada
                    </span>
                  ) : (
                    <span className="text-[10px] font-semibold text-slate-400">Sin firma</span>
                  )}
                </span>
                <span className="flex h-12 items-center justify-center overflow-hidden rounded-lg border border-dashed border-slate-200 bg-white">
                  {src ? (
                    <img
                      src={src}
                      alt={`Firma de ${slot.label}`}
                      data-fedup-signature-preview={preview?.dataUrl ? slot.slot : undefined}
                      className="max-h-11 max-w-full object-contain"
                      draggable={false}
                    />
                  ) : null}
                </span>
              </button>
              {locked && lockSlotId === slot.slot ? (
                <p role="status" className="mt-1 flex items-start gap-1.5 px-1 text-[11px] font-semibold text-slate-600">
                  <FiLock className="mt-0.5 shrink-0" aria-hidden="true" />
                  {OFFICIAL_LOCK_MESSAGE}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
